import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { X as XIcon, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Full-screen photo viewer. Dismisses on backdrop click or the ✕ button.
 * When `photos` (array of srcs) has more than one entry AND `onChange` is
 * supplied, prev/next arrow buttons appear at the left/right edges so
 * desktop/mouse users can move between photos without closing the modal.
 * Arrow keys (←/→) are wired up here too — ESC handling stays in the
 * parent (Result.jsx) because the parent owns the `src` state.
 *
 * Mobile back-gesture support: opening the lightbox pushes a browser
 * history sentinel so an iOS edge-swipe (or Android hardware back)
 * closes the viewer instead of navigating the SPA back to Gallery.
 * We consume popstate to close, and pop the sentinel ourselves when the
 * user closes via X/backdrop/ESC so the history stack stays clean.
 *
 * Backward-compatible: callers that pass only `src` + `onClose` get the
 * original single-photo behavior (no arrows, no keyboard nav).
 */
export const Lightbox = ({ src, alt = "", onClose, photos = null, onChange = null }) => {
  const { t } = useTranslation();

  // Memoize `list` so the useEffect below doesn't churn on every parent
  // render (Result.jsx rebuilds the photos array inline).
  const list = useMemo(() => (Array.isArray(photos) ? photos : []), [photos]);
  const currentIndex = list.indexOf(src);
  const canNav = list.length > 1 && typeof onChange === "function" && currentIndex >= 0;
  const atStart = currentIndex <= 0;
  const atEnd = currentIndex >= list.length - 1;

  const goPrev = () => {
    if (!canNav || atStart) return;
    onChange(list[currentIndex - 1]);
  };
  const goNext = () => {
    if (!canNav || atEnd) return;
    onChange(list[currentIndex + 1]);
  };

  // Arrow-key nav. Registered only when the lightbox is open AND we're in
  // multi-photo mode; otherwise we do nothing and let the parent's ESC
  // listener handle Escape. Logic is inlined here (rather than delegating
  // to `goPrev`/`goNext`) so the effect's deps stay simple — including
  // those callbacks would re-register the listener on every render.
  useEffect(() => {
    if (!src || !canNav) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") {
        if (currentIndex < list.length - 1) {
          e.preventDefault();
          onChange(list[currentIndex + 1]);
        }
      } else if (e.key === "ArrowLeft") {
        if (currentIndex > 0) {
          e.preventDefault();
          onChange(list[currentIndex - 1]);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [src, canNav, currentIndex, list, onChange]);

  // Mobile back-gesture / hardware-back support. Push a history sentinel
  // ONCE on open→close transitions (not on every prev/next slide switch)
  // so a back-swipe consumes it and closes the lightbox instead of
  // navigating out of the Result page. `openRef` tracks the last "open"
  // state so we only push/pop on real transitions.
  const openRef = useRef(false);
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    const nowOpen = !!src;
    if (nowOpen && !openRef.current) {
      openRef.current = true;
      window.history.pushState({ prairieLightbox: true }, "");
    } else if (!nowOpen && openRef.current) {
      // Programmatic close (X / backdrop / ESC): pop the sentinel we
      // pushed so the browser history stays consistent. The popstate that
      // fires from this history.back() is a no-op — openRef is already
      // false, so the popstate handler below won't re-fire onClose.
      openRef.current = false;
      if (window.history.state && window.history.state.prairieLightbox) {
        window.history.back();
      }
    }
  }, [src]);
  useEffect(() => {
    const onPop = () => {
      if (openRef.current) {
        // User invoked back (edge-swipe or hardware back) — consume the
        // sentinel and close the lightbox. Parent state clears via
        // onClose, and the src-tracking effect above skips its own
        // history.back() because openRef is already false.
        openRef.current = false;
        closeRef.current && closeRef.current();
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  if (!src) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("result.lightboxAria")}
      data-testid="photo-lightbox"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-up"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="lightbox-close"
        aria-label={t("common.close")}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition"
      >
        <XIcon className="w-5 h-5" strokeWidth={1.5} />
      </button>

      {/* Prev/next arrows — only rendered in multi-photo mode with an
          onChange callback. Positioned outside the image so they never
          overlap the plant. Disabled at either end. */}
      {canNav && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            disabled={atStart}
            data-testid="lightbox-prev"
            aria-label={t("result.carouselPrevAria")}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-6 h-6" strokeWidth={1.75} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            disabled={atEnd}
            data-testid="lightbox-next"
            aria-label={t("result.carouselNextAria")}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-6 h-6" strokeWidth={1.75} />
          </button>
          {/* Position pill so users know where they are in the set. */}
          <div
            data-testid="lightbox-index"
            className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-black/50 text-white text-xs px-3 py-1.5 backdrop-blur"
          >
            {currentIndex + 1}/{list.length}
          </div>
        </>
      )}

      <img
        src={src}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
        className="max-w-full max-h-full object-contain rounded-2xl"
      />
    </div>
  );
};

export default Lightbox;
